import type {
    ClosingImportRow,
    ClosingImportRowStatus,
} from '../components/closing-import-banner/closing-import-banner.types';
import type { IClosingSetupStep } from '../models/closing-setup.model';
import type { IPeriodBounds } from '../models/project-account.model';

/** The host's i18n lookup, so this stays a pure function. */
export type ClosingImportTranslate = (key: string, params?: Record<string, unknown>) => string;

const YEAR_DIGITS = 'YYYY'.length;

/**
 * The import banner's rows: the accounting data (connection and pull together), and the documents
 * from SharePoint, the latter only when the closing has any.
 */
export function closingImportRowsOf(
    steps: readonly IClosingSetupStep[],
    closingPeriod: IPeriodBounds | null,
    translate: ClosingImportTranslate,
): ClosingImportRow[] {
    const accounting = steps.filter((step) => step.key === 'ERP_CONNECT' || step.key === 'JOURNAL_SYNC');
    const journal = steps.find((step) => step.key === 'JOURNAL_SYNC');
    const documents = steps.find((step) => step.key === 'DOCUMENT_SYNC');
    const accountingStatus = combinedImportStatusOf(accounting);
    const rows: ClosingImportRow[] = [
        {
            key: 'accounting',
            label: translate('abcp.setup.import.rowAccounting'),
            detail:
                accountingStatus === 'done' ? loadedYearsLine(loadedYearsOf(journal, closingPeriod), translate) : null,
            status: accountingStatus,
        },
    ];
    if (documents !== undefined && documents.status !== 'SKIPPED') {
        rows.push({
            key: 'documents',
            label: translate('abcp.setup.import.rowDocuments'),
            detail: null,
            status: combinedImportStatusOf([documents]),
        });
    }

    return rows;
}

/**
 * The years the banner names, newest first: exactly what the server reports as loaded. A record
 * from before the server reported them names only the closing's own year, never a prior year it
 * cannot vouch for.
 */
export function loadedYearsOf(
    journal: IClosingSetupStep | undefined,
    closingPeriod: IPeriodBounds | null,
): readonly number[] {
    if (journal?.loadedYears !== undefined) {
        return [...new Set(journal.loadedYears)].sort((left, right) => right - left);
    }
    if (closingPeriod === null) {
        return [];
    }

    return [Number(closingPeriod.periodEnd.slice(0, YEAR_DIGITS))];
}

/** «2026 geladen», «2026 und 2025 geladen»; `null` when no year is known. */
function loadedYearsLine(years: readonly number[], translate: ClosingImportTranslate): string | null {
    if (years.length === 0) {
        return null;
    }
    if (years.length === 1) {
        return translate('abcp.setup.import.accountingDoneOne', { year: years[0] });
    }

    return translate('abcp.setup.import.accountingDone', {
        year: years.slice(0, -1).join(', '),
        prior: years[years.length - 1],
    });
}

/** One status for several server steps: any failure, then any work left, then done. */
export function combinedImportStatusOf(steps: readonly IClosingSetupStep[]): ClosingImportRowStatus {
    if (steps.some((step) => step.status === 'FAILED')) {
        return 'failed';
    }
    if (steps.every((step) => step.status === 'DONE' || step.status === 'SKIPPED')) {
        return 'done';
    }

    return steps.some((step) => step.status === 'RUNNING' || step.status === 'DONE') ? 'running' : 'pending';
}
